import { breakpointsTailwind, useEventListener } from '@vueuse/core'
import { computed, ref, type Ref } from 'vue'

type DeviceType
  = | 'mobile'
    | 'tablet'
    | 'console'
    | 'smarttv'
    | 'wearable'
    | 'embedded'
    | 'desktop'

/**
 * Stable SSR stand-in width from UA (not the real viewport). Used only before
 * `matchMedia` exists on the server.
 */
const SSR_WIDTH_BY_TYPE: Record<DeviceType, number> = {
  mobile: breakpointsTailwind.sm, // 640
  tablet: breakpointsTailwind.md, // 768
  desktop: breakpointsTailwind.xl, // 1280
  console: breakpointsTailwind.xl,
  smarttv: breakpointsTailwind.xl,
  wearable: breakpointsTailwind.sm,
  embedded: breakpointsTailwind.sm,
}

/**
 * Sync `(max-width: Npx)` — avoids VueUse `useMediaQuery` / `useBreakpoints`
 * staying `false` until mount (`useSupported` → `useMounted`), which caused
 * desktop shells to paint first on phones (search lupa CLS, etc.).
 *
 * Prefer CSS (`md:`, `lg:`) for show/hide. Use these refs when JS must branch
 * (modal vs popover, lift, scroll-lock teardown).
 */
function useMaxWidthMatches(maxPx: number, ssrWidth: number): Ref<boolean> {
  const query = `(max-width: ${maxPx}px)`

  const matches = ref(
    import.meta.client
      ? window.matchMedia(query).matches
      : ssrWidth <= maxPx,
  )

  if (import.meta.client) {
    const mql = window.matchMedia(query)
    matches.value = mql.matches
    useEventListener(mql, 'change', () => {
      matches.value = mql.matches
    })
  }

  return matches
}

/**
 * Viewport + UA helpers aligned with Tailwind breakpoints.
 *
 * | Flag | CSS equivalent | JS meaning |
 * |------|----------------|------------|
 * | `isMobile` | `< md` | max-width 767 |
 * | `isDesktop` | `>= md` | not isMobile |
 * | `isTablet` | `md`…`< lg` | 768–1023 |
 * | `isBelowLg` | `< lg` | max-width 1023 — header / search / mega-menu |
 * | `isLgUp` | `lg:` | min-width 1024 |
 */
export const useDevice = () => {
  const { $uaParser } = useNuxtApp()

  const deviceType = ($uaParser.getDevice().type ?? 'desktop') as DeviceType
  const ssrWidth = SSR_WIDTH_BY_TYPE[deviceType] ?? breakpointsTailwind.xl

  // Tailwind `md:` / `lg:` are min-width; “below” = one px under the token.
  const isMobile = useMaxWidthMatches(breakpointsTailwind.md - 1, ssrWidth)
  const isBelowLg = useMaxWidthMatches(breakpointsTailwind.lg - 1, ssrWidth)

  const isDesktop = computed(() => !isMobile.value)
  const isTablet = computed(() => !isMobile.value && isBelowLg.value)
  const isLgUp = computed(() => !isBelowLg.value)

  const osName = $uaParser.getOS().name ?? ''
  const isApple = computed(
    () => osName === 'iOS' || osName === 'Mac OS' || osName === 'macOS',
  )
  const isAndroid = computed(() => osName === 'Android')

  const kind = computed<'mobile' | 'tablet' | 'desktop'>(() => {
    if (isMobile.value) return 'mobile'
    if (isTablet.value) return 'tablet'
    return 'desktop'
  })

  return {
    deviceType,
    isMobile,
    isTablet,
    isDesktop,
    isBelowLg,
    isLgUp,
    isApple,
    isAndroid,
    kind,
  }
}
